import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dataTypeService } from '../services/data-type.service';
import { CreateDataTypeDto, UpdateDataTypeDto } from '../types/dtos/DataTypeDto';

const DATA_TYPES_KEY = 'data-types';

/**
 * Hook to fetch all active data types
 * Data types are system-wide and cached for 1 hour
 */
export const useDataTypes = () => {
  return useQuery({
    queryKey: [DATA_TYPES_KEY],
    queryFn: async () => {
      const response = await dataTypeService.findAll();
      // Service already returns CustomResponseEntity, extract the data array
      return response.data;
    },
    staleTime: 1000 * 60 * 60, // 1 hour - data types don't change often
    gcTime: 1000 * 60 * 60 * 2, // 2 hours
  });
};

/**
 * Hook to fetch a single data type by ID
 */
export const useDataType = (id: string) => {
  return useQuery({
    queryKey: [DATA_TYPES_KEY, id],
    queryFn: async () => {
      const response = await dataTypeService.findById(id);
      return response.data;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 60,
  });
};

/**
 * Hook to create a new data type
 */
export const useCreateDataType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateDataTypeDto) => {
      const response = await dataTypeService.create(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DATA_TYPES_KEY] });
    },
  });
};

/**
 * Hook to update a data type
 */
export const useUpdateDataType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateDataTypeDto }) => {
      const response = await dataTypeService.update(id, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [DATA_TYPES_KEY] });
      queryClient.invalidateQueries({ queryKey: [DATA_TYPES_KEY, id] });
    },
  });
};

/**
 * Hook to delete a data type
 */
export const useDeleteDataType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await dataTypeService.delete(id);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DATA_TYPES_KEY] });
    },
  });
};

/**
 * Helper function to match MIME type against DataType validators
 * @param mimeType - File MIME type (e.g., "video/mp4")
 * @param validator - DataType validator pattern (e.g., "video/*" or "application/pdf")
 * @returns true if the MIME type matches the validator pattern
 */
export const matchesMimeType = (mimeType: string, validator: string): boolean => {
  // Handle exact matches
  if (validator === mimeType) return true;

  // Handle wildcard patterns (e.g., "video/*")
  if (validator.endsWith('/*')) {
    const prefix = validator.slice(0, -2);
    return mimeType.startsWith(prefix);
  }

  // Handle comma-separated validators (e.g., "audio/*,video/*")
  if (validator.includes(',')) {
    const validators = validator.split(',').map((v) => v.trim());
    return validators.some((v) => matchesMimeType(mimeType, v));
  }

  return false;
};

